import React, {useCallback, useEffect, useRef, useState} from 'react'
import axios from 'axios'
import toaster from '../../../Utils/toaster'
import {CommonLoading} from '../../../Utils/commonLoading'
import {BulkVosFinanceView} from './BulkVosFinanceView'
import {BatchDetail} from './bulkVosTypes'
import {
  BulkVosFinanceBatchOption,
  BulkVosFinanceFilterData,
  buildDefaultFinanceFilter,
  normalizeFinanceBatchList,
  normalizeFinanceFilterSelection,
} from './bulkVosFinanceFilter'

const API_URL = process.env.REACT_APP_API_URL

function extractBatchListPayload(data: Record<string, unknown> | undefined): unknown {
  if (!data) return []
  const msg = data.result ?? data.Result
  if (Array.isArray(msg)) return msg
  if (msg && typeof msg === 'object') {
    const inner = msg as Record<string, unknown>
    return inner.result ?? inner.Result ?? []
  }
  return []
}

export function FinanceBulkVosPage() {
  const [allBatches, setAllBatches] = useState<BulkVosFinanceBatchOption[]>([])
  const [batch, setBatch] = useState<BatchDetail | null>(null)
  const [listLoading, setListLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const pendingBatchesRef = useRef<BulkVosFinanceBatchOption[]>([])
  const [filterData, setFilterData] = useState<BulkVosFinanceFilterData>(() =>
    buildDefaultFinanceFilter([]),
  )

  const applyFilter = useCallback(
    (next: BulkVosFinanceFilterData) => {
      setFilterData(normalizeFinanceFilterSelection(next, allBatches))
    },
    [allBatches],
  )

  const loadAllBatches = useCallback(async () => {
    setListLoading(true)
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        axios.get(`${API_URL}/bulk-vos/batches`, {params: {approvedTab: false}}),
        axios.get(`${API_URL}/bulk-vos/batches`, {params: {approvedTab: true}}),
      ])
      const pending = normalizeFinanceBatchList(extractBatchListPayload(pendingRes.data))
      pendingBatchesRef.current = pending
      const approved = normalizeFinanceBatchList(extractBatchListPayload(approvedRes.data))
      const merged = [...pending, ...approved]
      const byId = new Map<number, BulkVosFinanceBatchOption>()
      merged.forEach((b) => byId.set(b.batchId, b))
      setAllBatches(Array.from(byId.values()))
    } catch {
      toaster('error', 'Unable to load batches')
      setAllBatches([])
    } finally {
      setListLoading(false)
    }
  }, [])

  const loadBatchDetail = useCallback(async () => {
    if (!filterData.batchId) {
      setBatch(null)
      return
    }
    setDetailLoading(true)
    try {
      const res = await axios.get(`${API_URL}/bulk-vos/batches/${filterData.batchId}`)
      if (res.data?.status === 'S' || res.data?.Status === 'S') {
        const raw = (res.data.result ?? res.data.Result) as BatchDetail & {
          PortalToken?: string
        }
        setBatch({
          ...raw,
          portalToken: raw.portalToken ?? raw.PortalToken,
        })
      } else {
        toaster('error', res.data?.text || res.data?.Text || 'Unable to load batch')
        setBatch(null)
      }
    } catch {
      toaster('error', 'Unable to load batch')
      setBatch(null)
    } finally {
      setDetailLoading(false)
    }
  }, [filterData.batchId])

  useEffect(() => {
    void loadAllBatches()
  }, [loadAllBatches])

  useEffect(() => {
    if (listLoading) return
    setFilterData((prev) => {
      if (prev.batchId != null) {
        const stillExists = allBatches.some((b) => b.batchId === prev.batchId)
        if (stillExists) return prev
      }
      return buildDefaultFinanceFilter(allBatches, pendingBatchesRef.current)
    })
  }, [listLoading, allBatches])

  useEffect(() => {
    void loadBatchDetail()
  }, [loadBatchDetail])

  useEffect(() => {
    if (filterData.batchId == null) return
    if (!allBatches.some((b) => b.batchId === filterData.batchId)) {
      setFilterData((prev) => ({...prev, batchId: null}))
      setBatch(null)
    }
  }, [allBatches, filterData.batchId])

  const readOnly = filterData.batchTab === 'approved'

  return (
    <>
      <div id='bulk-vos-tabpanel-batches'>
        {listLoading ? (
          <CommonLoading />
        ) : allBatches.length === 0 ? (
          <div className='text-center text-muted py-10'>No batches found.</div>
        ) : (
          <BulkVosFinanceView
            batch={batch}
            detailLoading={detailLoading}
            readOnly={readOnly}
            filterData={filterData}
            availableBatches={allBatches}
            onFilterApply={applyFilter}
            onReload={loadBatchDetail}
            onApproved={loadAllBatches}
          />
        )}
      </div>
    </>
  )
}
